/**
 * 헤더 컴포넌트
 * - 앱 제목 및 네비게이션
 * - 로그인 상태 표시
 * - 모바일: 로고 + LangSwitcher만 표시 (네비게이션은 BottomNav로 대체)
 * - 테마 토글 버튼 (라이트/다크/시스템)
 */
import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, useReducedMotion } from 'framer-motion';
import { useAuthStore } from '../../store/authStore';
import { Button } from '../ui/Button';
import { useOfflineSync } from '../../hooks/useOfflineSync';
import { useScrollAwareHeader } from '../../hooks/useScrollAwareHeader';
import LangSwitcher from '../ui/LangSwitcher';
import UserMenu from './UserMenu';
import { NotificationBell } from './NotificationBell';
import AnimatedDisc from '../ui/AnimatedDisc';
import { useTheme } from '../../hooks/useTheme';

/** 헤더를 완전 숨기는 경로 (에디터/내보내기) */
const HEADER_HIDDEN_PATTERNS = [
  /^\/albums\/[^/]+\/edit/,
  /^\/albums\/[^/]+\/export/,
  /^\/albums\/new/,
];

/** 테마별 아이콘 — 외부 라이브러리 의존 없이 SVG 인라인 */
function ThemeIcon({ theme }: { theme: 'light' | 'dark' | 'system' }) {
  if (theme === 'dark') {
    /* 달 아이콘 */
    return (
      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
      </svg>
    );
  }
  if (theme === 'light') {
    /* 태양 아이콘 */
    return (
      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="12" cy="12" r="5" />
        <line x1="12" y1="1" x2="12" y2="3" />
        <line x1="12" y1="21" x2="12" y2="23" />
        <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
        <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
        <line x1="1" y1="12" x2="3" y2="12" />
        <line x1="21" y1="12" x2="23" y2="12" />
        <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
        <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
      </svg>
    );
  }
  /* system — 모니터 아이콘 */
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
      <line x1="8" y1="21" x2="16" y2="21" />
      <line x1="12" y1="17" x2="12" y2="21" />
    </svg>
  );
}

/** 테마 순환: light → dark → system → light */
const THEME_CYCLE = ['light', 'dark', 'system'] as const;
const THEME_LABEL: Record<string, string> = {
  light: '라이트 모드',
  dark: '다크 모드',
  system: '시스템 설정',
};

const Header: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { isAuthenticated, user, logout } = useAuthStore();
  const { online, pendingCount, handleSync } = useOfflineSync();
  const { theme, setTheme } = useTheme();
  const { visible, headerRef } = useScrollAwareHeader();
  const prefersReduced = useReducedMotion();

  // 에디터/내보내기/데모 경로에서는 헤더 완전 숨김
  if (HEADER_HIDDEN_PATTERNS.some((p) => p.test(location.pathname))) {
    return null;
  }

  /** 테마를 순환하며 전환 */
  const cycleTheme = () => {
    const idx = THEME_CYCLE.indexOf(theme as typeof THEME_CYCLE[number]);
    const next = THEME_CYCLE[(idx + 1) % THEME_CYCLE.length];
    setTheme(next);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  /** 현재 경로와 일치하면 active 스타일 반환 */
  const navLinkClass = (path: string) => {
    const isActive = location.pathname === path || location.pathname.startsWith(path + '/');
    return isActive
      ? 'bg-white/20 text-white font-semibold border-b-2 border-white rounded-lg px-3 py-2'
      : 'bg-transparent text-white/75 hover:bg-white/20 hover:text-white transition-colors rounded-lg px-3 py-2';
  };

  return (
    <motion.header
      ref={headerRef}
      className="bg-brand-header dark:bg-brand-header-dark text-white shadow-lg sticky top-0 z-50"
      animate={{ y: visible ? 0 : '-100%' }}
      transition={prefersReduced ? { duration: 0 } : { duration: 0.22, ease: [0.25, 0.1, 0.25, 1] }}
      aria-hidden={!visible || undefined}
    >
      <div className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between gap-2 min-w-0">
          {/* 로고 및 제목 */}
          <Link to="/" aria-label="홈으로 이동" className="group flex items-center space-x-2 xs:space-x-3 hover:opacity-90 transition-opacity min-w-0">
            <span className="shrink-0 inline-flex items-center justify-center w-9 h-9 xs:w-10 xs:h-10 rounded-full bg-white/95 shadow-md ring-1 ring-white/40">
              <AnimatedDisc size="md" hoverSpin label="MD STUDIO 로고" variant="primary" />
            </span>
            <div className="min-w-0">
              <h1 className="text-base xs:text-lg md:text-2xl font-bold whitespace-nowrap truncate">MD STUDIO</h1>
              <p className="hidden xl:block text-xs text-primary-100">미니디스크 라벨 제작기</p>
            </div>
          </Link>

          {/* 데스크톱/태블릿 네비게이션 */}
          <nav aria-label={t('header.mainNav')} className="hidden md:flex items-center space-x-1 lg:space-x-2">
            <Link to="/gallery" className="focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white rounded-md">
              <Button variant="default" size="sm" className={`${navLinkClass('/gallery')} min-h-[44px]`}>
                {t('header.gallery')}
              </Button>
            </Link>
            <Link to="/templates" className="focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white rounded-md">
              <Button variant="default" size="sm" className={`${navLinkClass('/templates')} min-h-[44px]`}>
                {t('header.explore', '탐색')}
              </Button>
            </Link>
            {isAuthenticated && (
              <>
                <Link to="/albums" className="focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white rounded-md">
                  <Button variant="default" size="sm" className={`${navLinkClass('/albums')} min-h-[44px]`}>
                    {t('header.albums')}
                  </Button>
                </Link>
                <Link to="/albums/new?fresh=1" className="focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white rounded-md">
                  <Button variant="default" size="sm" className="min-h-[44px] bg-white/20 hover:bg-white/30 text-white font-semibold rounded-lg px-3 py-2 border border-white/40">
                    + {t('header.create', '만들기')}
                  </Button>
                </Link>
              </>
            )}

            {isAuthenticated ? (
              <div className="flex items-center space-x-2 ml-2 lg:ml-4 border-l border-white/30 pl-2 lg:pl-4">
                <NotificationBell />
                <UserMenu user={user} onLogout={handleLogout} />
              </div>
            ) : (
              <>
                <Link to="/signup" className="focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white rounded-md">
                  <Button variant="default" size="sm" className="min-h-[44px] bg-white/20 hover:bg-white/30 text-white font-semibold rounded-lg px-3 py-2 border border-white/40">
                    {t('common.signup', '회원가입')}
                  </Button>
                </Link>
                <Link to="/login">
                  <Button variant="default" size="sm" className={`${navLinkClass('/login')} min-h-[44px]`}>
                    {t('common.login')}
                  </Button>
                </Link>
              </>
            )}

            {/* 오프라인/동기화 대기 배지 */}
            {!online && (
              <span className="hidden md:inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                {t('header.offline')}
              </span>
            )}
            {pendingCount > 0 && online && (
              <button
                onClick={handleSync}
                className="hidden md:inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800 hover:bg-primary-200 transition-colors"
                title="동기화 실행"
              >
                {t('header.syncPending', { count: pendingCount })}
              </button>
            )}

            {/* 테마 토글 버튼 (데스크톱) */}
            <button
              onClick={cycleTheme}
              aria-label={`테마 전환 — 현재: ${THEME_LABEL[theme]}`}
              title={`테마 전환 — 현재: ${THEME_LABEL[theme]}`}
              className="inline-flex items-center justify-center min-w-[44px] min-h-[44px] rounded-lg text-white/80 hover:text-white hover:bg-white/20 transition-colors"
            >
              <ThemeIcon theme={theme} />
            </button>

            {/* 언어 선택 */}
            <LangSwitcher />
          </nav>

          {/* 모바일 우측: 테마 토글 + 언어 선택 (네비는 BottomNav로) */}
          <div className="flex md:hidden items-center space-x-1 xs:space-x-2 shrink-0">
            {!isAuthenticated && (
              <Link
                to="/login"
                className="text-sm text-white hover:text-white px-2 xs:px-3 py-2 rounded-lg hover:bg-white/20 transition-colors whitespace-nowrap"
              >
                {t('common.login')}
              </Link>
            )}
            {/* 테마 토글 버튼 (모바일) */}
            <button
              onClick={cycleTheme}
              aria-label={`테마 전환 — 현재: ${THEME_LABEL[theme]}`}
              title={`테마 전환 — 현재: ${THEME_LABEL[theme]}`}
              className="inline-flex items-center justify-center min-w-[44px] min-h-[44px] rounded-lg text-white/80 hover:text-white hover:bg-white/20 transition-colors"
            >
              <ThemeIcon theme={theme} />
            </button>
            <LangSwitcher />
          </div>
        </div>
      </div>
    </motion.header>
  );
};

export default Header;
